import React from "react";
import AsyncSelect from "react-select/async";

/**
 * DataMappingSingleSelect Component
 *
 * A reusable single-select dropdown component using react-select with single-select
 * functionality for the Data Mapping page.
 *
 * Props:
 * - loadOptions: Function to load options asynchronously
 * - selectedValues: Array of selected values (for consistency with multi-select)
 * - onChange: Function to handle selection changes (values: string[]) => void
 * - placeholder: Optional placeholder text
 * - isDisabled: Optional boolean to disable the dropdown
 * - className: Optional CSS class name
 */
const DataMappingSingleSelect = ({
  loadOptions = [],
  selectedValues = [],
  onChange,
  placeholder = "Select option...",
  isDisabled = false,
  className = "",
}) => {
  const loadingMessage = "Loading Tables...";

  // Ensure selectedValues is always an array and filter out invalid values
  const validSelectedValues = Array.isArray(selectedValues)
    ? selectedValues.filter(
        value => value && typeof value === "string" && value.trim() !== ""
      )
    : [];

  // Convert selectedValues array to react-select format (take first value for single select)
  const selectedOption =
    validSelectedValues.length > 0
      ? { value: validSelectedValues[0], label: validSelectedValues[0] }
      : null;

  // Handle selection changes
  const handleChange = selectedOption => {
    if (!selectedOption) {
      onChange([]);
      return;
    }

    // Extract value from selected option and return as array for consistency
    const values = [selectedOption.value];
    onChange(values);
  };

  // Custom styles for react-select to match the application's design
  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: "42px",
      border: state.isFocused ? "1px solid #006dee" : "1px solid #b0bac4",
      borderRadius: "6px",
      boxShadow: state.isFocused ? "0 0 0 3px rgba(0, 109, 238, 0.2)" : "none",
      fontSize: "15px",
      lineHeight: "22px",
      fontWeight: 500,
      backgroundColor: isDisabled ? "#f5f5f5" : "#fff",
      transition:
        "border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out",
      "&:hover": {
        borderColor: state.isFocused ? "#006dee" : "#8e99a4",
      },
      opacity: isDisabled ? 0.7 : 1,
      pointerEvents: isDisabled ? "none" : "auto",
      cursor: isDisabled ? "not-allowed" : "default",
    }),
    menu: provided => ({
      ...provided,
      zIndex: 9999,
      borderRadius: "6px",
      border: "1px solid #b0bac4",
      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
      overflow: "hidden",
    }),
    menuList: provided => ({
      ...provided,
      padding: "4px 0",
    }),
    option: (provided, state) => ({
      ...provided,
      padding: "10px 14px",
      fontSize: "14px",
      cursor: "pointer",
      backgroundColor: state.isSelected
        ? "#006dee"
        : state.isFocused
          ? "rgba(0, 109, 238, 0.06)"
          : "transparent",
      color: state.isSelected ? "#fff" : "#111827",
      "&:active": {
        backgroundColor: state.isSelected
          ? "#006dee"
          : "rgba(0, 109, 238, 0.1)",
      },
    }),
    placeholder: provided => ({
      ...provided,
      color: "#777e7e",
      fontSize: "15px",
      fontWeight: 500,
    }),
    singleValue: provided => ({
      ...provided,
      color: "#111827",
      fontSize: "15px",
      fontWeight: 500,
    }),
    container: provided => ({
      ...provided,
      width: "100%",
    }),
  };

  return (
    <div className={`data-mapping-singleselect-container ${className}`}>
      <AsyncSelect
        isMulti={false}
        loadOptions={async inputValue => {
          if (typeof loadOptions !== "function") {
            console.error(
              "DataMappingSingleSelect - loadOptions is not a function:",
              loadOptions
            );
            return [];
          }
          try {
            const result = await loadOptions(inputValue);
            return result;
          } catch (error) {
            console.error(
              "DataMappingSingleSelect - loadOptions error:",
              error
            );
            return [];
          }
        }}
        value={selectedOption}
        onChange={handleChange}
        styles={customStyles}
        placeholder={placeholder}
        isClearable={true}
        isSearchable={true}
        isDisabled={isDisabled}
        className={className || "data-mapping-singleselect"}
        classNamePrefix="data-mapping-singleselect"
        noOptionsMessage={() => "No options available"}
        loadingMessage={() => loadingMessage}
        cacheOptions={true}
        defaultOptions={true}
      />
    </div>
  );
};

export default DataMappingSingleSelect;
